import React from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  EntitlementIcon,
  OpenSidebarButton,
  SandboxLozenge,
  TrialLozenge,
  UpgradePremiumTrial,
  UpgradeAdvancedTrial,
} from '@css-xp/base';
import { pluralize } from '@css-xp/ppc-utils';
import { CartProductSchema } from '../../types/store-types';
import { ORDER_PAGE_SIDEBAR } from '../../../types';

const ProductNameContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

const ProductEntitlementNumber = styled.div`
  color: ${token('color.text.subtle')};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

export const EntitlementItemCell = ({
  name,
  productId,
  logoId,
  entitlementNumber,
  inTrial,
  shouldShowSandboxStatus,
  inUpgradeTrial,
  shouldShowIncludedProducts,
  orgInstances,
  isApp,
  includedProductsComponent,
}: {
  name: string;
  productId?: string;
  logoId?: string;
  entitlementNumber?: string;
  inTrial?: boolean;
  shouldShowSandboxStatus?: boolean;
  inUpgradeTrial?: boolean;
  shouldShowIncludedProducts?: boolean;
  orgInstances?: CartProductSchema[];
  includedProductsComponent?: React.ReactNode;
  isApp?: boolean;
}) => {
  if (!productId && !logoId) return null;
  return (
    <div className="product-rows-container--product-wrapper--product-content flex">
      <EntitlementIcon
        productId={productId}
        logoId={logoId}
        className="product-rows-container--entitlement-icon"
        isCloud
      />

      <ProductNameContainer>
        <div className="product-rows-container--product-name flex">
          <div
            className={classNames({
              'product-rows-container--product-name-text': inTrial,
            })}
            title={name}
          >
            {name}
          </div>
          {shouldShowSandboxStatus && (
            <div data-testId={`product-rows-sandbox-entitlement-${entitlementNumber}`}>
              <SandboxLozenge />
            </div>
          )}
          {inTrial && (
            <div data-testId={`product-rows-trial-entitlement-${entitlementNumber}`}>
              <TrialLozenge />
            </div>
          )}
          {inUpgradeTrial && (
            <div data-testId={`product-rows-upgrade-trial-entitlement-${entitlementNumber}`}>
              {isApp ? <UpgradeAdvancedTrial /> : <UpgradePremiumTrial />}
            </div>
          )}
        </div>
        {entitlementNumber && <ProductEntitlementNumber>{entitlementNumber}</ProductEntitlementNumber>}
        {includedProductsComponent}
        {!includedProductsComponent && shouldShowIncludedProducts && orgInstances && orgInstances?.length > 0 && (
          <Box paddingBlockStart="space.025">
            <OpenSidebarButton
              sidebar={ORDER_PAGE_SIDEBAR.INCLUDED_PRODUCTS}
              sidebarOptions={{ sideBarDistanceFromTop: '0px' }}
              payload={{
                version: 2,
                productName: name,
                entitlementNumber: entitlementNumber,
                includedProductsList: orgInstances?.map((instance: CartProductSchema) => ({
                  productId: instance?.productId,
                  logoId: instance?.logoId,
                  productName: instance?.name,
                  cloudSiteHostname: instance?.cloudSite,
                  entitlementNumber: instance?.entitlementNumber,
                })),
              }}
              spacing="compact"
              testId="uof-included-products-sidebar-button"
              label="Included products"
            >
              {pluralize(orgInstances.length, 'included product')}
            </OpenSidebarButton>
          </Box>
        )}
      </ProductNameContainer>
    </div>
  );
};
